import { io, Socket } from 'socket.io-client';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

// Real-time event types
export interface RealtimeEvent {
  type: 'new_booking' | 'payment_completed' | 'review_submitted' | 'user_registered' | 'system_alert';
  data: any;
  timestamp: string;
}

export interface SystemAlert {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  data?: any;
  timestamp: string;
}

// Real-time service class
class RealtimeService {
  private socket: Socket | null = null;
  private isConnected = false;
  private connectionRetries = 0;
  private maxRetries = 5;
  private retryDelay = 1000; // Start with 1 second
  private eventListeners: Map<string, Function[]> = new Map();

  constructor() {
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.emit = this.emit.bind(this);
    this.on = this.on.bind(this);
    this.off = this.off.bind(this);
  }

  connect(token?: string) {
    if (this.socket?.connected) return;

    const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    
    this.socket = io(baseURL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('🟢 Real-time connection established');
      this.isConnected = true;
      this.connectionRetries = 0;
      this.retryDelay = 1000;
      
      // Emit connection success to listeners
      this.notifyListeners('connection', { status: 'connected' });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔴 Real-time connection lost:', reason);
      this.isConnected = false;
      this.notifyListeners('connection', { status: 'disconnected', reason });
      
      // Auto-reconnect for certain reasons
      if (reason === 'io server disconnect') {
        // Server disconnected, don't reconnect automatically
        return;
      }
      
      this.handleReconnection();
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Real-time connection error:', error);
      this.isConnected = false;
      this.notifyListeners('connection', { status: 'error', error: error.message });
      this.handleReconnection();
    });

    // Admin-specific events
    this.socket.on('new_booking', (data) => {
      console.log('📅 New booking received:', data);
      this.notifyListeners('new_booking', data);
    });

    this.socket.on('payment_completed', (data) => {
      console.log('💰 Payment completed:', data);
      this.notifyListeners('payment_completed', data);
    });

    this.socket.on('review_submitted', (data) => {
      console.log('⭐ New review submitted:', data);
      this.notifyListeners('review_submitted', data);
    });

    this.socket.on('user_registered', (data) => {
      console.log('👤 New user registered:', data);
      this.notifyListeners('user_registered', data);
    });

    this.socket.on('system_alert', (data) => {
      console.log('🚨 System alert:', data);
      this.notifyListeners('system_alert', data);
    });

    this.socket.on('booking_cancelled', (data) => {
      console.log('❌ Booking cancelled:', data);
      this.notifyListeners('booking_cancelled', data);
    });

    this.socket.on('property_created', (data) => {
      console.log('🏢 New property created:', data);
      this.notifyListeners('property_created', data);
    });

    this.socket.on('review_flagged', (data) => {
      console.log('🚩 Review flagged:', data);
      this.notifyListeners('review_flagged', data);
    });
  }

  private handleReconnection() {
    if (this.connectionRetries >= this.maxRetries) {
      console.error('🔴 Max reconnection attempts reached');
      this.notifyListeners('connection', { 
        status: 'failed', 
        message: 'Unable to establish connection after multiple attempts' 
      });
      return;
    }

    this.connectionRetries++;
    const delay = this.retryDelay * Math.pow(2, this.connectionRetries - 1); // Exponential backoff
    
    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.connectionRetries}/${this.maxRetries})`);
    
    setTimeout(() => {
      if (!this.isConnected && this.socket) {
        this.socket.connect();
      }
    }, delay);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.connectionRetries = 0;
    }
  }

  emit(event: string, data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    } else {
      console.warn('🟡 Cannot emit event - socket not connected:', event);
    }
  }

  on(event: string, callback: Function) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push(callback);
  }

  off(event: string, callback?: Function) {
    if (!callback) {
      this.eventListeners.delete(event);
      return;
    }

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyListeners(event: string, data: any) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      retries: this.connectionRetries,
      maxRetries: this.maxRetries,
    };
  }

  // Join admin room for admin-specific events
  joinAdminRoom() {
    this.emit('join_admin_room', {});
  }

  // Leave admin room
  leaveAdminRoom() {
    this.emit('leave_admin_room', {});
  }
}

// Singleton instance
export const realtimeService = new RealtimeService();

// React hook for real-time functionality
export function useRealtime(events: string[] = []) {
  const [connectionStatus, setConnectionStatus] = useState(realtimeService.getConnectionStatus());
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const eventHandlersRef = useRef<Map<string, Function>>(new Map());

  useEffect(() => {
    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    
    if (token) {
      realtimeService.connect(token);
      realtimeService.joinAdminRoom();
    }

    // Connection status handler
    const connectionHandler = (data: any) => {
      setConnectionStatus(realtimeService.getConnectionStatus());
      
      if (data.status === 'connected') {
        toast({
          title: "Connected",
          description: "Real-time updates are now active",
          duration: 3000,
        });
      } else if (data.status === 'disconnected') {
        toast({
          title: "Connection Lost",
          description: "Attempting to reconnect...",
          variant: "destructive",
          duration: 5000,
        });
      } else if (data.status === 'failed') {
        toast({
          title: "Connection Failed",
          description: data.message || "Unable to establish real-time connection",
          variant: "destructive",
        });
      }
    };

    realtimeService.on('connection', connectionHandler);

    // Setup event handlers for requested events
    events.forEach(eventName => {
      const handler = (data: any) => {
        setLastEvent({
          type: eventName as any,
          data,
          timestamp: new Date().toISOString(),
        });

        // Handle specific events
        switch (eventName) {
          case 'new_booking':
            queryClient.invalidateQueries({ queryKey: ['/admin/bookings'] });
            queryClient.invalidateQueries({ queryKey: ['/admin/dashboard/stats'] });
            toast({
              title: "New Booking",
              description: `New booking for ${data.property_name}`,
            });
            break;

          case 'payment_completed':
            queryClient.invalidateQueries({ queryKey: ['/admin/bookings'] });
            queryClient.invalidateQueries({ queryKey: ['/admin/dashboard/stats'] });
            toast({
              title: "Payment Received",
              description: `Payment of ${data.amount} completed`,
            });
            break;

          case 'review_submitted':
            queryClient.invalidateQueries({ queryKey: ['/admin/reviews'] });
            toast({
              title: "New Review",
              description: `${data.rating}-star review submitted`,
            });
            break;

          case 'user_registered':
            queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
            queryClient.invalidateQueries({ queryKey: ['/admin/dashboard/stats'] });
            toast({
              title: "New User",
              description: `${data.name} joined the platform`,
            });
            break;

          case 'system_alert':
            queryClient.invalidateQueries({ queryKey: ['/admin/alerts'] });
            toast({
              title: data.title,
              description: data.message,
              variant: data.priority === 'critical' || data.priority === 'high' ? 'destructive' : 'default',
            });
            break;

          case 'review_flagged':
            queryClient.invalidateQueries({ queryKey: ['/admin/reviews'] });
            toast({
              title: "Review Flagged",
              description: "A review requires moderation",
              variant: "destructive",
            });
            break;

          case 'booking_cancelled':
            queryClient.invalidateQueries({ queryKey: ['/admin/bookings'] });
            queryClient.invalidateQueries({ queryKey: ['/admin/dashboard/stats'] });
            toast({
              title: "Booking Cancelled",
              description: `Booking for ${data.property_name} was cancelled`,
              variant: "destructive",
            });
            break;

          case 'property_created':
            queryClient.invalidateQueries({ queryKey: ['/admin/properties'] });
            toast({
              title: "New Property",
              description: `${data.name} was added to the platform`,
            });
            break;
        }
      };

      eventHandlersRef.current.set(eventName, handler);
      realtimeService.on(eventName, handler);
    });

    return () => {
      // Cleanup event handlers
      realtimeService.off('connection', connectionHandler);
      
      eventHandlersRef.current.forEach((handler, eventName) => {
        realtimeService.off(eventName, handler);
      });
      
      // Don't disconnect here as other components might be using it
      // realtimeService.disconnect();
    };
  }, [events.join(','), queryClient, toast]);

  const emit = (event: string, data: any) => {
    realtimeService.emit(event, data);
  };

  const disconnect = () => {
    realtimeService.leaveAdminRoom();
    realtimeService.disconnect();
  };

  return {
    connectionStatus,
    lastEvent,
    emit,
    disconnect,
    isConnected: connectionStatus.connected,
  };
}

// Hook specifically for admin dashboard real-time updates
export function useAdminRealtime() {
  const adminEvents = [
    'new_booking',
    'payment_completed',
    'review_submitted',
    'user_registered',
    'system_alert',
    'review_flagged',
    'booking_cancelled',
    'property_created'
  ];

  return useRealtime(adminEvents);
}

// Hook for system health monitoring
export function useSystemMonitoring() {
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);

  useEffect(() => {
    const healthHandler = (data: any) => {
      setSystemHealth(data);
    };

    const alertHandler = (alert: SystemAlert) => {
      setAlerts(prev => [alert, ...prev.slice(0, 49)]); // Keep last 50 alerts
    };

    realtimeService.on('system_health', healthHandler);
    realtimeService.on('system_alert', alertHandler);

    return () => {
      realtimeService.off('system_health', healthHandler);
      realtimeService.off('system_alert', alertHandler);
    };
  }, []);

  return {
    systemHealth,
    alerts,
    clearAlerts: () => setAlerts([]),
  };
}

export default realtimeService;
